import { useEffect } from 'react';
import { useRouter } from 'next/router';
import { adWriteApi } from '../../../../api/adWriteApi';
import { fireToast } from 'helpers/Toasts';
import { useAdDetailsContext } from 'contexts/AdDetailsContext';
import { useVerificationContext } from 'contexts/VerificationContext';
import { useUserContext } from 'contexts/UserContext';
import { pushToDataLayer } from 'services/gtmService';

import { VerifyTooltip } from 'components/PageWrappers/SharedComponents/VerifyTooltip/VerifyTooltip';
import { Button } from 'components/Toolkit/Button/Button';
import { useAuthOptions } from 'hooks/useAuthOptions';
import { formatAuthorizationHeader } from 'helpers/auth';

interface RevealSendMessageProps {
  adId: number;
  size?: 'SMALL' | 'LARGE' | 'GROW';
  testId?: string;
  trackingId?: string;
  clickSource?: string;
  disabled?: boolean;
}

const RevealSendMessage = (props: RevealSendMessageProps) => {
  const {
    adId,
    size = 'SMALL',
    testId = 'reveal-send-message',
    trackingId,
    clickSource = '',
    disabled = false,
  } = props;
  const { setIsSendMessageModalOpen, setMessage, message } =
    useAdDetailsContext();
  const { triggerAfterVerify, setTriggerAfterVerify } =
    useVerificationContext();
  const { user } = useUserContext();
  const { handleLogin, accessToken } = useAuthOptions();
  const { asPath } = useRouter();

  useEffect(() => {
    if (triggerAfterVerify && user && user.isMinimumVerified) {
      setTriggerAfterVerify(false);
      handleReveal();
    }
    // TODO Clean up this effect's dependencies. We're disabling this lint error for now so we can
    // clean up the lint logs. Ideally we should rewrite this code to be less error prone and trust
    // the lint rule's judgement.
    // https://distilledsch.tpondemand.com/RestUI/Board.aspx#page=userstory/98606
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [triggerAfterVerify]);

  async function handleReveal() {
    pushToDataLayer({ event: 'message_intent', source: clickSource });
    try {
      if (!message) {
        const {
          data: { message },
        } = await adWriteApi.getSendMessageInitialValue(
          adId,
          formatAuthorizationHeader(accessToken),
        );
        setMessage(message);
      }
      setIsSendMessageModalOpen(true);
    } catch (_error) {
      fireToast({
        type: 'ERROR',
        text: 'Oops! Something went wrong, please try again later',
      });
      return false;
    }
  }
  return (
    <>
      {user && !user.isMinimumVerified ? (
        <VerifyTooltip
          arrowPosition={['right', 'right', 'center']}
          tooltipPosition={'above'}
          text="You’ll need to verify your email or phone number before you can send enquiries"
        >
          <Button
            disabled={disabled}
            size={size}
            data-testid={testId}
            data-tracking={trackingId}
          >
            Send Message
          </Button>
        </VerifyTooltip>
      ) : (
        <Button
          disabled={disabled}
          size={size}
          onClick={user ? handleReveal : () => handleLogin(asPath)}
          data-testid={testId}
          data-tracking={trackingId}
        >
          Send Message
        </Button>
      )}
    </>
  );
};

export { RevealSendMessage };
